import type { TBreakdownDataCol } from './types';

export const toNum = (v: unknown): number | null =>
  typeof v === 'number' && isFinite(v) ? v : null;

export const sumValues = (...values: (number | null | undefined)[]): number | null => {
  let sum = 0;
  let hasValue = false;
  values.forEach((v) => {
    const n = toNum(v);
    if (n === null) return;
    sum += n;
    hasValue = true;
  });
  // All-null inputs should render blank cells, not 0, in the workbook template.
  return hasValue ? sum : null;
};

export const formatCellValue = (v: unknown): number | string => {
  if (v === null || v === undefined || v === '') return '';
  return typeof v === 'number' ? v : String(v);
};

export const collectSortedMonths = <TMonthRow>(
  rawData: TMonthRow[],
  getMonthKey: (row: TMonthRow) => string,
): string[] => {
  const monthSet = new Set<string>();
  rawData.forEach((row) => monthSet.add(getMonthKey(row)));
  return Array.from(monthSet).sort();
};

export const groupMonthsByYear = (months: string[]): Map<string, string[]> => {
  const map = new Map<string, string[]>();
  months.forEach((m) => {
    const year = m.slice(0, 4);
    if (!map.has(year)) map.set(year, []);
    map.get(year)!.push(m);
  });
  return map;
};

export const formatMonthLabel = (yyyyMM: string): string => {
  const MONTH_NAMES = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
  ];
  const monthIdx = parseInt(yyyyMM.slice(5, 7), 10) - 1;
  return MONTH_NAMES[monthIdx] ?? yyyyMM.slice(5, 7);
};

export const buildDataCols = (yearMap: Map<string, string[]>): TBreakdownDataCol[] => {
  const dataCols: TBreakdownDataCol[] = [];
  yearMap.forEach((months, year) => {
    months.forEach((m) => dataCols.push({ type: 'month', month: m }));
    // Year total column is appended after that year's months in the template.
    dataCols.push({ type: 'total', year });
  });
  return dataCols;
};
